import { checkVisibility, getPositionEcf, parseTLE, tleEpochAgeDays } from '../loader/engine/orbits';
import { tleChecksum, type TleSatellite } from '../loader/engine/tle';
import type { ContactWindow } from '../loader/types';
import type { RfimsSite } from './registry';
import { horizonAt } from '../twin/site/terrainMath';

/** Band candidates, not receiver assignments. Status/source review: 24 October 2026. */
export const RFIMS_VISIBILITY_CANDIDATES = [
  {
    id: 51850,
    name: 'GOES-18',
    signal: 'HRIT/EMWIN · 1694.1 MHz',
    source: 'https://ospo.noaa.gov/operations/goes/hrit/index.html',
  },
  {
    id: 60133,
    name: 'GOES-19',
    signal: 'HRIT/EMWIN · 1694.1 MHz',
    source: 'https://ospo.noaa.gov/operations/goes/hrit/index.html',
  },
  {
    id: 38771,
    name: 'Metop-B',
    signal: 'AHRPT design · 1701.3 MHz',
    source: 'https://www.esa.int/Applications/Observing_the_Earth/Meteorological_missions/MetOp/Operations',
  },
  {
    id: 43689,
    name: 'Metop-C',
    signal: 'AHRPT design · 1701.3 MHz',
    source: 'https://www.esa.int/Applications/Observing_the_Earth/Meteorological_missions/MetOp/Operations',
  },
] as const;

export interface RfimsVisibilityResult {
  candidate: (typeof RFIMS_VISIBILITY_CANDIDATES)[number];
  epoch?: string;
  unavailable?: string;
  windows: ContactWindow[];
}

export interface RfimsVisibilityOptions {
  observerEllipsoidM?: number;
  minElevationDeg?: number;
  horizon?: number[];
}

export function scanRfimsWindows(
  startMs: number,
  sample: (timeMs: number) => { elevation: number; azimuth: number } | null,
  options: RfimsVisibilityOptions = {},
): ContactWindow[] {
  const windows: ContactWindow[] = [];
  const endMs = startMs + 86_400_000,
    step = 30_000,
    mask = options.minElevationDeg ?? 5;
  let rise: number | null = null,
    maxElevation = -90;
  const close = (time: number) => {
    if (rise !== null && time - rise >= 60_000)
      windows.push({ startMs: rise, endMs: time, durationMin: (time - rise) / 60_000, maxElevation });
    rise = null;
    maxElevation = -90;
  };
  for (let time = startMs; time <= endMs; time += step) {
    const look = sample(time);
    if (!look || !Number.isFinite(look.elevation + look.azimuth))
      throw new Error('Propagation unavailable within requested window');
    const visible = look.elevation >= Math.max(mask, horizonAt(look.azimuth, options.horizon ?? []));
    if (visible) {
      if (rise === null) rise = time;
      maxElevation = Math.max(maxElevation, look.elevation);
    } else close(time);
  }
  close(endMs);
  return windows;
}

export function predictRfimsVisibility(
  site: RfimsSite,
  satellites: TleSatellite[],
  startMs: number,
  options: RfimsVisibilityOptions = {},
): RfimsVisibilityResult[] {
  return RFIMS_VISIBILITY_CANDIDATES.map((candidate) => {
    const tle = satellites.find((s) => s.noradId === candidate.id);
    if (!tle) return { candidate, windows: [], unavailable: 'No current orbital elements in the feed' };
    if (
      [tle.tle1, tle.tle2].some(
        (line) =>
          line.length !== 69 ||
          Number(line.slice(2, 7)) !== candidate.id ||
          tleChecksum(line) !== Number(line[68]),
      )
    )
      return { candidate, windows: [], unavailable: 'Invalid orbital identity or checksum' };
    const ageDays = tleEpochAgeDays(tle.tle1, startMs);
    if (!Number.isFinite(ageDays) || ageDays < -1 || ageDays > 7)
      return {
        candidate,
        windows: [],
        unavailable: 'Orbital epoch is outside the accepted −1 to 7 day range',
      };
    const satrec = parseTLE({ line1: tle.tle1, line2: tle.tle2 });
    if (!satrec || Number(tle.tle2.slice(2, 7)) !== candidate.id)
      return { candidate, windows: [], unavailable: 'Invalid orbital elements' };
    const epoch = new Date(startMs - ageDays * 86_400_000).toISOString();
    try {
      const windows = scanRfimsWindows(
        startMs,
        (time) => {
          const position = getPositionEcf(satrec, time);
          return position
            ? checkVisibility(position, {
                lat: site.lat,
                lon: site.lon,
                heightKm: (options.observerEllipsoidM ?? 0) / 1000,
              })
            : null;
        },
        options,
      );
      return { candidate, epoch, windows };
    } catch {
      return {
        candidate,
        epoch,
        windows: [],
        unavailable: 'Propagation unavailable within requested window',
      };
    }
  });
}
